import { apiClient } from './client';
import type { ConversationType } from '@pm-next/shared-types';

export interface ApiConversationListItem {
  id: number;
  title: string | null;
  type: ConversationType;
  created_at: string;
  updated_at: string;
  _count: { chat_message: number };
  total_tokens?: number;
  total_response_time_ms?: number;
}

export interface ApiFunctionResult {
  id: number;
  function_name: string;
  function_calls: string | null;
  result_data: string | null;
  visualization_type: string | null;
  execution_time: number | null;
}

export interface ApiChatMessage {
  id: number;
  role: string;
  content: string | null;
  created_at: string;
  response_time: number | null;
  error_message: string | null;
  tokens?: number;
  chat_function_result?: ApiFunctionResult[];
}

export interface ApiConversationWithMessages {
  id: number;
  title: string | null;
  type: ConversationType;
  created_at: string;
  updated_at: string;
  chat_message: ApiChatMessage[];
  total_tokens: number;
  total_cost: number;
  total_response_time_ms: number;
}

export interface CreateConversationBody {
  title?: string;
  type?: ConversationType;
}

export interface CreateMessageBody {
  role: 'user' | 'assistant' | 'system';
  content?: string | null;
}

export async function fetchConversations(
  type?: ConversationType
): Promise<ApiConversationListItem[]> {
  const params = type ? { type } : {};
  const res = await apiClient.get<ApiConversationListItem[]>('/chat/conversations', { params });
  return res.data;
}

export async function createConversation(
  body: CreateConversationBody = {}
): Promise<ApiConversationWithMessages> {
  const res = await apiClient.post<ApiConversationWithMessages>('/chat/conversations', body);
  return res.data;
}

export async function fetchConversationWithMessages(
  id: number
): Promise<ApiConversationWithMessages> {
  const res = await apiClient.get<ApiConversationWithMessages>(`/chat/conversations/${id}`);
  return res.data;
}

export async function addMessage(
  conversationId: number,
  body: CreateMessageBody
): Promise<{ id: number; role: string; content: string | null; created_at: string }> {
  const res = await apiClient.post(`/chat/conversations/${conversationId}/messages`, body);
  return res.data;
}

export async function updateConversationTitle(
  conversationId: number,
  title: string
): Promise<{ id: number; title: string | null; updated_at: string }> {
  const res = await apiClient.patch<{ id: number; title: string | null; updated_at: string }>(
    `/chat/conversations/${conversationId}`,
    { title }
  );
  return res.data;
}

export async function archiveConversation(
  conversationId: number
): Promise<{ id: number; title: string | null; type: ConversationType; archived_at: string }> {
  const res = await apiClient.delete<{
    id: number;
    title: string | null;
    type: ConversationType;
    archived_at: string;
  }>(`/chat/conversations/${conversationId}`);
  return res.data;
}

export async function downloadReportPdf(conversationId: number, filename?: string): Promise<void> {
  const res = await apiClient.get(`/reports/conversations/${conversationId}/pdf`, {
    responseType: 'blob',
  });

  const blob = new Blob([res.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `report_${conversationId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
